import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Link,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
  StatHelpText,
  FormLabel,
  SimpleGrid,
  Heading,
  useColorMode,
  Img,
} from '@chakra-ui/react'
import numeral from 'numeral'
import React, { useState } from 'react'
import { v4 as uuid } from 'uuid'
import AnimatedNumber from 'animated-number-react'
import { AddIcon, DeleteIcon, InfoIcon, RepeatIcon } from '@chakra-ui/icons'
import { BigNumber } from 'bignumber.js'
import { FaExternalLinkAlt } from 'react-icons/fa'

const ImpermanentLossCalculator = () => {
  const { isOpen, onToggle } = useDisclosure()
  const { colorMode } = useColorMode()
  const [assets, setAssets] = useState([
    {
      uuid: 'c2037d93-1f28-4bfc-8495-0afb54e7b962',
      name: 'BAL',
      priceChange: 400,
      weight: 50,
    },
    {
      uuid: '9e4b081f-75ff-4ac5-9bf3-bc67e7ffeb0f',
      name: 'WBTC',
      priceChange: 150,
      weight: 34,
    },
    {
      uuid: 'c70d6fc8-c03b-47dd-abb0-41c1eaa73457',
      name: 'WETH',
      priceChange: 400,
      weight: 16,
    },
  ])

  const getAssetPriceChangeInX = (assetPriceChangeinPercent) =>
    Math.abs(assetPriceChangeinPercent / 100 + 1)

  const holdValue = assets
    .map(
      (asset) =>
        (getAssetPriceChangeInX(asset.priceChange) * asset.weight) / 100
    )
    .reduce((assetA, assetB) => assetA + assetB, 0)

  const poolValue = assets
    .map((asset) =>
      Math.pow(getAssetPriceChangeInX(asset.priceChange), asset.weight / 100)
    )
    .reduce((assetA, assetB) => assetA * assetB, 1)

  const calculateImpermanentLoss = (poolValue, holdValue) => {
    const BNPoolValue = new BigNumber(poolValue)
    const BNHoldValue = new BigNumber(holdValue)

    return BNPoolValue.dividedBy(BNHoldValue).minus(1).dp(4)
  }

  const impermanentLoss = calculateImpermanentLoss(poolValue, holdValue)

  const pnl =
    assets
      .map((asset) => (asset.priceChange / 100) * (asset.weight / 100))
      .reduce((a, b) => a + b, 0) - Math.abs(impermanentLoss)

  const updateName = (asset, e) => {
    const newAssets = assets.map((newAsset) => {
      if (newAsset.uuid === asset.uuid) {
        return { ...newAsset, name: e.target.value }
      }
      return newAsset
    })
    setAssets(newAssets)
  }

  const updatePriceChange = (asset, value) => {
    const newAssets = assets.map((newAsset) => {
      if (newAsset.uuid === asset.uuid) {
        return { ...newAsset, priceChange: value }
      }
      return newAsset
    })
    setAssets(newAssets)
  }

  const updateWeight = (asset, e) => {
    const newAssets = assets.map((newAsset) => {
      if (newAsset.uuid === asset.uuid) {
        return { ...newAsset, weight: e.target.value }
      }
      return newAsset
    })
    setAssets(newAssets)
  }

  const totalPoolWeight = assets
    .map((asset) => asset.weight)
    .reduce((assetA, assetB) => Number(assetA) + Number(assetB), 0)

  const addAsset = () => {
    setAssets([
      ...assets,
      {
        uuid: uuid(),
        priceChange: 0,
        weight: 100 - totalPoolWeight >= 2 ? 100 - totalPoolWeight : 2,
      },
    ])
  }

  const deleteAsset = (thisAsset) =>
    setAssets([...assets.filter((asset) => asset.uuid !== thisAsset.uuid)])

  const clearAssets = () =>
    setAssets([
      {
        uuid: uuid(),
        name: '',
        priceChange: 0,
        weight: 50,
      },
      {
        uuid: uuid(),
        name: '',
        priceChange: 0,
        weight: 50,
      },
    ])

  return (
    <Flex flexDirection={{ base: 'column', md: 'row' }}>
      <Box
        w={{ md: '800px' }}
        p={{ base: 0, md: 5 }}
        m={6}
        mt={0}
        borderWidth={{ base: 0, md: 1 }}
        rounded="lg"
        borderColor="linear(to-r, brand.300 ,brand.600)"
      >
        <Box>
          <SimpleGrid columns={3} minChildWidth="250px">
            <Stat>
              <StatLabel fontSize="xl">Impermanent Loss</StatLabel>
              {assets.length > 1 ? (
                <>
                  <StatNumber fontSize="5xl">
                    <AnimatedNumber
                      value={impermanentLoss}
                      formatValue={(value) =>
                        numeral(Math.abs(value)).format('0.00%')
                      }
                      duration={500}
                    />
                  </StatNumber>
                  <StatHelpText>
                    <Button
                      color={colorMode === 'dark' ? 'gray.100' : 'gray.700'}
                      size="xs"
                      variant="link"
                      textDecoration="underline"
                      onClick={() => onToggle()}
                      my={1}
                    >
                      {isOpen ? 'Hide' : 'Show'} calculation
                    </Button>
                  </StatHelpText>
                </>
              ) : (
                <Flex alignItems="center" color="blue.500">
                  <InfoIcon mr="2" /> Add at least 2 assets m8
                </Flex>
              )}
            </Stat>
            {/* <Stat>
            <StatLabel fontSize="xl">Profit/Loss</StatLabel>
            {assets.length > 0 ? (
              <>
                <StatNumber fontSize="5xl">
                  <AnimatedNumber
                    value={pnl}
                    formatValue={(value) =>
                      numeral(Math.abs(value)).format('0.00%')
                    }
                    duration={500}
                  />
                  <StatHelpText>(Total gain - IL)</StatHelpText>
                </StatNumber>
              </>
            ) : (
              <Flex alignItems="center" color="blue.500">
                <InfoIcon mr="2" /> Add at least 1 asset m8
              </Flex>
            )}
          </Stat> */}
            {totalPoolWeight !== 100 && (
              <Stat color={totalPoolWeight !== 100 ? 'brand.400' : 'green.500'}>
                <StatLabel fontSize="xl">Total Pool Weight</StatLabel>
                <StatNumber fontSize="5xl">
                  {numeral(totalPoolWeight / 100).format('0%')}
                </StatNumber>
                <StatHelpText>Must equal 100!</StatHelpText>
              </Stat>
            )}
          </SimpleGrid>
          {isOpen && (
            <Box my={3} p={3} borderRadius={6} borderWidth={1}>
              <Box mb={3}>
                <Heading size="sm">Calculating Impermanent Loss</Heading>
                <Text fontStyle="italic">
                  Impermanent Loss = Value of Pool / Asset Value if Held - 1
                </Text>
                <Text>
                  {numeral(impermanentLoss).format('0.00')} ={' '}
                  {numeral(poolValue).format('0.00')} /{' '}
                  {numeral(holdValue).format('0.00')} - 1
                </Text>
                <Text>
                  (Impermanent Loss is formatted from{' '}
                  {numeral(impermanentLoss).format('0.00')} to a % and made
                  positive (losses cannot be negative) →{' '}
                  {numeral(Math.abs(impermanentLoss)).format('0.00%')})
                </Text>
              </Box>
              <Box mb={3}>
                <Heading size="sm">Calculating 'Value of Pool'</Heading>
                <Text fontStyle="italic">
                  To get the Pool Value, take each asset, put its weight divided
                  by hundred to the power of the price change. Then multiply all
                  these.
                </Text>
                <Text>
                  {numeral(poolValue).format('0.00')} ={' '}
                  {assets.map(
                    (asset, i) =>
                      `${getAssetPriceChangeInX(asset.priceChange)}^${asset.weight
                      } / 100${assets.length !== i + 1 ? ' * ' : ''}`
                  )}
                </Text>
              </Box>
              <Box mb={3}>
                <Heading size="sm">Calculating 'Asset Value if Held'</Heading>
                <Text fontStyle="italic">
                  To get the Hold Value, take each asset, and multiply its price
                  change by its weight, then divide by 100. Then add all these up.
                </Text>
                <Text>
                  {numeral(holdValue).format('0.00')} ={' '}
                  {assets.map(
                    (asset, i) =>
                      `${getAssetPriceChangeInX(asset.priceChange)} x ${asset.weight
                      } / 100${assets.length !== i + 1 ? ' + ' : ''}`
                  )}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
        <Divider my={6} display={{ base: 'none', md: 'block' }} />
        <Box display={{ base: 'block', md: 'none' }} mb={3}>
          <Button
            size="sm"
            onClick={addAsset}
            leftIcon={<AddIcon />}
            variant="solid"
            mr={3}
            colorScheme={assets.length <= 1 ? 'blue' : 'gray'}
          >
            Add Asset
          </Button>
          <Button
            size="sm"
            onClick={clearAssets}
            variant="outline"
            leftIcon={<RepeatIcon />}
          >
            Clear Assets
          </Button>
        </Box>
        <Box mb={3} display={{ base: 'none', md: 'block' }}>
          <Stack isInline spacing={4}>
            <Text w={175} fontWeight="bold">
              Asset
            </Text>
            <Text w={175} fontWeight="bold">
              Price Changes by...
            </Text>
            <Flex
              alignItems="center"
              w={175}
              fontWeight="bold"
              color={totalPoolWeight !== 100 ? 'brand.400' : 'inherit'}
            >
              <Text>Pool Weight</Text>
            </Flex>
          </Stack>
        </Box>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 1 }} spacingX={3}>
          {assets.map((asset, i) => (
            <Box
              key={asset.uuid}
              borderWidth={{ base: 1, md: 0 }}
              p={{ base: 3, md: 0 }}
              mb={{ base: 3, md: 0 }}
              borderRadius={6}
            >
              <Stack
                align={{ base: 'start', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                spacing={4}
              >
                <FormControl w={175}>
                  <FormLabel mb={2} display={{ base: 'block', md: 'none' }}>
                    Asset {i + 1}
                  </FormLabel>
                  <Input
                    w={125}
                    placeholder="eg BAL"
                    value={asset.name}
                    onChange={(e) => updateName(asset, e)}
                  />
                </FormControl>
                <FormControl w={175}>
                  <FormLabel mb={2} display={{ base: 'block', md: 'none' }}>
                    Price changes by...
                  </FormLabel>
                  <InputGroup w={100}>
                    <Input
                      w={100}
                      type="number"
                      placeholder="0"
                      step="25"
                      min="-100"
                      value={asset.priceChange}
                      onChange={(e) => {
                        updatePriceChange(asset, e.target.value)
                      }}
                    />
                    <InputRightElement
                      color={colorMode === 'dark' ? 'gray.100' : 'gray.700'}
                      children="%"
                      fontWeight="bold"
                    />
                  </InputGroup>
                </FormControl>
                <FormControl w={175}>
                  <FormLabel
                    mb={2}
                    display={{ base: 'block', md: 'none' }}
                    color={totalPoolWeight !== 100 && 'brand.400'}
                  >
                    Pool weight
                  </FormLabel>
                  <InputGroup w={100}>
                    <Input
                      w={100}
                      type="number"
                      step="1"
                      min="2"
                      max="100"
                      placeholder={50}
                      focusBorderColor={
                        totalPoolWeight !== 100 ? 'brand.400' : 'blue.500'
                      }
                      borderColor={
                        totalPoolWeight !== 100 ? 'brand.400' : 'inherit'
                      }
                      value={asset.weight}
                      onChange={(e) => updateWeight(asset, e)}
                    />
                    <InputRightElement
                      color={colorMode === 'dark' ? 'gray.100' : 'gray.700'}
                      borderLeftColor={
                        totalPoolWeight !== 100 ? 'brand.400' : 'gray.200'
                      }
                      children="%"
                      fontWeight="bold"
                    />
                  </InputGroup>
                </FormControl>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deleteAsset(asset)}
                  leftIcon={<DeleteIcon />}
                >
                  Remove
                </Button>
              </Stack>
              <Divider my={6} display={{ base: 'none', md: 'block' }} />
            </Box>
          ))}
        </SimpleGrid>
        <Flex alignItems="center" justifyContent="space-between" wrap="wrap">
          <Box mb={{ base: 3, sm: 0 }}>
            <Button
              size="sm"
              onClick={addAsset}
              leftIcon={<AddIcon />}
              variant="solid"
              mr={3}
              colorScheme={assets.length <= 1 ? 'blue' : 'gray'}
            >
              Add Asset
            </Button>
            <Button
              size="sm"
              onClick={clearAssets}
              variant="outline"
              leftIcon={<RepeatIcon />}
            >
              Clear Assets
            </Button>
          </Box>
          <Link
            textDecoration="underline"
            href="https://pools.balancer.exchange"
            target="_blank"
            rel="noopener noreferrer"
          >
            Manage Pools on Balancer
          </Link>
        </Flex>
      </Box>
      <Box
        w={{ md: '300px' }}
        p={{ base: 0, md: 5 }}
        m={6}
        mt={0}
        borderWidth={{ base: 0, md: 1 }}
        rounded="lg"
        borderColor="linear(to-r, brand.300 ,brand.600)"
      >
        <Link href="https://bit.ly/autonolas"><Img src={require('../../images/autonolas-logo.svg')} width={75} mb={3} /></Link>
        <Heading size="md" mb={1}>Web3-native Software Services for DAOs and Protocols</Heading>
        <Text mb={3}>Be early to one of the strongest emerging ecosystems in the category of Web3-native compute.</Text>
        <Link href="https://bit.ly/autonolas" textDecoration="underline"
        >Learn more</Link>
      </Box>
    </Flex>
  )
}

export default ImpermanentLossCalculator
